import { useState } from "preact/hooks";
import { Chip } from "./Chip.tsx";

interface Props {
  id: string;
  values: readonly string[];
  onValues: (values: string[]) => void;
  /** The remove button's accessible name for one value. */
  removeLabel: (value: string) => string;
  placeholder?: string;
  title?: string;
}

/** `values` plus the comma-separated, trimmed, non-empty `text` parts they don't have yet. */
function added(values: readonly string[], text: string): string[] {
  const next = [...values];
  for (const v of text.split(",").map((x) => x.trim())) {
    if (v && !next.includes(v)) {
      next.push(v);
    }
  }
  return next;
}

/**
 * A list of short values as chips, with a text box for more: Enter or a comma adds what's typed (so pasting a
 * comma-separated list adds each), leaving the box adds it too, and Backspace in the empty box takes the last one back.
 */
export function TokenInput({ id, values, onValues, removeLabel, placeholder, title }: Props) {
  const [draft, setDraft] = useState("");
  const commit = (text: string) => {
    setDraft("");
    const next = added(values, text);
    if (next.length > values.length) {
      onValues(next);
    }
  };
  return (
    <div class="flex min-h-7 w-full flex-wrap items-center gap-1 rounded-md border border-zinc-300 bg-white px-1 py-0.5 shadow-xs ios:min-h-9 ios:rounded-[10px] ios:border-transparent ios:bg-zinc-950/6 ios:shadow-none focus-within:border-blue-600 focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-blue-600 has-disabled:bg-zinc-50">
      {values.map((v) => (
        <Chip key={v} removeLabel={removeLabel(v)} onRemove={() => onValues(values.filter((x) => x !== v))}>
          {v}
        </Chip>
      ))}
      <input
        id={id}
        title={title}
        placeholder={values.length === 0 ? placeholder : undefined}
        class="h-5 min-w-24 flex-1 border-0 bg-transparent px-1 text-xs text-zinc-900 outline-none placeholder:text-zinc-400"
        value={draft}
        onInput={(e) => {
          const text = e.currentTarget.value;
          const cut = text.lastIndexOf(",");
          if (cut === -1) {
            setDraft(text);
          } else {
            // Everything up to the last comma is done; the rest is still being typed.
            commit(text.slice(0, cut));
            setDraft(text.slice(cut + 1));
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && draft.trim()) {
            e.preventDefault(); // add the value, don't submit the form
            commit(draft);
          } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
            e.preventDefault();
            setDraft(values.at(-1) ?? "");
            onValues(values.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
      />
    </div>
  );
}
